/**
 * ============================================================================
 * DESKTOP UI - TYPE DEFINITIONS
 * ============================================================================
 *
 * This file contains all TypeScript interfaces and types for the Desktop UI
 * component library. These types define the configuration schema that users
 * will use to declaratively create their desktop environments.
 *
 * @packageDocumentation
 */

import { ComponentType, ReactElement, ReactNode, RefObject } from "react";

/* ==========================================================================
   WINDOW TYPES
   ========================================================================== */

/**
 * The type of window determines its behavior and appearance.
 *
 * @remarks
 * - `app`: Renders a React component inside the window. Best for custom UIs.
 * - `browser`: Renders an iframe with browser-style chrome (URL bar, reload).
 */
export type WindowType = "app" | "browser";

/* ==========================================================================
   DIMENSIONS - With Discriminant for Runtime Safety
   ========================================================================== */

/**
 * Named position slots for window placement.
 * The actual pixel values are calculated at runtime based on screen size.
 */
export type WindowPosition =
  | "center"
  | "top-left"
  | "top-right"
  | "bottom-left"
  | "bottom-right"
  | "top-center"
  | "bottom-center"
  | "left-center"
  | "right-center";

/**
 * Percentage-based dimensions relative to the available viewport size.
 *
 * - `{ ratio: 80 }` — both width and height are 80% of available viewport.
 * - `{ width: 80, height: 60 }` — width is 80%, height is 60%.
 *
 * Values are integers/floats in `(0, 100]`. When supplied, percentage
 * dimensions take precedence over `width`/`height` strings on desktop and
 * are recomputed as the viewport resizes — until the user manually resizes
 * the window or has a persisted size from a prior session, in which case
 * the user's size always wins.
 *
 * Has no effect on mobile (windows auto-maximize there).
 */
export type DimensionPercentage =
  | { ratio: number }
  | { width: number; height: number };

/**
 * Type guard for the `{ ratio }` shorthand variant of {@link DimensionPercentage}.
 */
export function isDimensionPercentageRatio(
  dp: DimensionPercentage
): dp is { ratio: number } {
  return "ratio" in dp;
}

/**
 * Resolve a {@link DimensionPercentage} to absolute pixel dimensions given
 * the available viewport width/height. Returns `null` when input is invalid.
 */
export function resolveDimensionPercentage(
  dp: DimensionPercentage,
  availableWidth: number,
  availableHeight: number
): { width: number; height: number } | null {
  const clamp = (n: number) => Math.max(0, Math.min(100, n));
  if (isDimensionPercentageRatio(dp)) {
    if (!Number.isFinite(dp.ratio)) return null;
    const r = clamp(dp.ratio) / 100;
    return { width: availableWidth * r, height: availableHeight * r };
  }
  if (!Number.isFinite(dp.width) || !Number.isFinite(dp.height)) return null;
  return {
    width: availableWidth * (clamp(dp.width) / 100),
    height: availableHeight * (clamp(dp.height) / 100),
  };
}

/**
 * Fixed dimensions for a window (non-responsive).
 *
 * @example
 * ```ts
 * const dimensions: FixedDimensions = {
 *   responsive: false,
 *   width: '800px',
 *   height: '600px',
 *   position: 'center', // Smart positioning based on screen size
 * }
 * ```
 */
export interface FixedDimensions {
  /** Discriminant: false = fixed dimensions */
  responsive: false;
  /** Width of the window. Accepts px, vw, vh, or % */
  width: string;
  /** Height of the window. Accepts px, vw, vh, or % */
  height: string;
  /**
   * Percentage of available viewport size. When set, takes precedence over
   * `width`/`height` and auto-tracks viewport changes until the user
   * resizes the window. Ignored on mobile.
   */
  dimensionPercentage?: DimensionPercentage;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Minimum height in pixels */
  minHeight?: number;
  /**
   * Named position for the window. Calculated at runtime based on screen size.
   * Defaults to 'center' if not specified.
   * @example 'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'
   */
  position?: WindowPosition;
  /**
   * Initial X position from left edge (overrides position if set).
   * @deprecated Use `position` instead for responsive placement.
   */
  initialX?: number;
  /**
   * Initial Y position from top edge (overrides position if set).
   * @deprecated Use `position` instead for responsive placement.
   */
  initialY?: number;
}

/**
 * Dimension values for a specific breakpoint.
 */
export interface BreakpointDimensions {
  /** Width of the window. Accepts px, vw, vh, or % */
  width: string;
  /** Height of the window. Accepts px, vw, vh, or % */
  height: string;
  /**
   * Percentage of available viewport size. When set, takes precedence over
   * `width`/`height` for this breakpoint and auto-tracks viewport changes
   * until the user resizes the window.
   */
  dimensionPercentage?: DimensionPercentage;
  /** Minimum width in pixels */
  minWidth?: number;
  /** Minimum height in pixels */
  minHeight?: number;
  /**
   * Named position for the window. Calculated at runtime based on screen size.
   * Defaults to 'center' if not specified.
   */
  position?: WindowPosition;
  /**
   * Initial X position from left edge (overrides position if set).
   * @deprecated Use `position` instead for responsive placement.
   */
  initialX?: number;
  /**
   * Initial Y position from top edge (overrides position if set).
   * @deprecated Use `position` instead for responsive placement.
   */
  initialY?: number;
}

/**
 * Responsive dimension configuration for different screen sizes.
 *
 * @example
 * ```ts
 * const dimensions: ResponsiveDimensions = {
 *   responsive: true,
 *   mobile: { width: '100vw', height: '100vh' },
 *   tablet: { width: '80vw', height: '80vh' },
 *   desktop: { width: '800px', height: '600px' },
 * }
 * ```
 */
export interface ResponsiveDimensions {
  /** Discriminant: true = responsive dimensions */
  responsive: true;
  /** Dimensions for mobile devices (< 768px) */
  mobile?: BreakpointDimensions;
  /** Dimensions for tablets (768px - 1024px) */
  tablet?: BreakpointDimensions;
  /** Dimensions for desktop (> 1024px) */
  desktop?: BreakpointDimensions;
}

/**
 * Window dimensions - either fixed or responsive.
 * Use the `responsive` discriminant to determine which type at runtime.
 */
export type WindowDimensions = FixedDimensions | ResponsiveDimensions;

/**
 * Type guard to check if dimensions are responsive.
 */
export function isResponsiveDimensions(
  dims: WindowDimensions
): dims is ResponsiveDimensions {
  return dims.responsive === true;
}

/**
 * Type guard to check if dimensions are fixed.
 */
export function isFixedDimensions(
  dims: WindowDimensions
): dims is FixedDimensions {
  return dims.responsive === false;
}

/* ==========================================================================
   WINDOW CONFIGURATION
   ========================================================================== */

/**
 * Base configuration shared by all window types.
 */
interface WindowConfigBase {
  /** Unique identifier for the window */
  id: string;
  /** Title displayed in the window's title bar */
  title: string;
  /** Icon displayed in the title bar (ReactElement) */
  icon?: ReactElement;
  /** Window dimensions (fixed or responsive) */
  dimensions?: WindowDimensions;
  /** Whether the window can be minimized (default: true) */
  canMinimize?: boolean;
  /** Whether the window can be maximized (default: true on desktop) */
  canMaximize?: boolean;
  /** Whether the window can be resized (default: true on desktop) */
  canResize?: boolean;
  /** Hide the title bar completely */
  hideTitleBar?: boolean;
  /** Open the window maximized (default: false) */
  openMaximized?: boolean;
}

/**
 * Configuration for an "app" type window that renders a React component.
 *
 * @example
 * ```ts
 * const terminalWindow: AppWindowConfig = {
 *   id: 'terminal',
 *   type: 'app',
 *   title: 'Terminal',
 *   icon: <Terminal className="w-4 h-4" />,
 *   component: TerminalComponent,
 *   componentProps: { theme: 'dark' },
 * }
 * ```
 */
export interface AppWindowConfig extends WindowConfigBase {
  type: "app";
  /** The React component to render inside the window */
  component: ComponentType<Record<string, unknown>>;
  /** Props to pass to the component */
  componentProps?: Record<string, unknown>;
}

/**
 * Configuration for a "browser" type window that renders an iframe.
 *
 * @example
 * ```ts
 * const webWindow: BrowserWindowConfig = {
 *   id: 'browser',
 *   type: 'browser',
 *   title: 'https://example.com',
 *   icon: <Globe className="w-4 h-4" />,
 *   url: 'https://example.com',
 *   showReloadButton: true,
 * }
 * ```
 */
export interface BrowserWindowConfig extends WindowConfigBase {
  type: "browser";
  /** URL to load in the iframe */
  url: string;
  /** Show a reload button in the title bar (default: true) */
  showReloadButton?: boolean;
  /** Iframe sandbox attributes */
  sandbox?: string;
  /** Iframe allow attributes (for permissions) */
  allow?: string;
  /** Iframe referrer policy */
  referrerPolicy?: ReferrerPolicy;
}

/**
 * Union type for all window configurations.
 */
export type WindowConfig = AppWindowConfig | BrowserWindowConfig;

/**
 * Type guard to check if a window config is an app window.
 */
export function isAppWindow(config: WindowConfig): config is AppWindowConfig {
  return config.type === "app";
}

/**
 * Type guard to check if a window config is a browser window.
 */
export function isBrowserWindow(
  config: WindowConfig
): config is BrowserWindowConfig {
  return config.type === "browser";
}

/* ==========================================================================
   DESKTOP ICON CONFIGURATION
   ========================================================================== */

/**
 * Configuration for a desktop icon.
 *
 * @remarks
 * Each icon is linked to a window by its `windowId`. When the icon is
 * clicked, the corresponding window is opened.
 *
 * @example
 * ```ts
 * const terminalIcon: DesktopIconConfig = {
 *   id: 'terminal-icon',
 *   windowId: 'terminal',
 *   icon: <Terminal className="w-8 h-8 text-green-400" />,
 *   label: 'Terminal',
 * }
 * ```
 */
export interface DesktopIconConfig {
  /** Unique identifier for the icon */
  id: string;
  /** ID of the window to open when clicked */
  windowId: string;
  /**
   * The icon to display.
   * Can be a ReactElement (recommended) or an emoji string.
   *
   * @example
   * ```tsx
   * // Using a Lucide icon
   * icon: <Terminal className="w-8 h-8 text-green-400" />
   *
   * // Using an emoji
   * icon: "📁"
   * ```
   */
  icon: ReactElement | string;
  /** Label displayed below the icon */
  label: string;
  /** Initial X position (optional, auto-arranged if not set) */
  initialX?: number;
  /** Initial Y position (optional, auto-arranged if not set) */
  initialY?: number;
  /**
   * Optional callback invoked when the icon is clicked (after opening/focusing the window).
   * Useful for icons that act as shortcuts (e.g., Trash opening Scout at /trash).
   */
  onIconClick?: () => void;
  /**
   * Optional callback to determine if the "active" indicator should be shown.
   * When provided, this overrides the default behavior (showing dot when window is open).
   * Useful for shortcut icons (e.g., Trash should only show dot when Scout is at /trash).
   * @param windowIsOpen - Whether the linked window is currently open
   * @returns Whether to show the active indicator
   */
  isActiveIcon?: (windowIsOpen: boolean) => boolean;
  /**
   * Pin this icon to a fixed position in the dock.
   * Pinned icons are separated from the main dock icons by a divider
   * and cannot be reordered, removed from the dock, or moved to the desktop.
   *
   * - `"end"` — pinned to the far right of the dock (e.g., Trash)
   */
  pinned?: "end";
  /**
   * Optional callback that controls whether this icon (and its window) are visible.
   * When provided, the icon and window are only rendered if this returns true.
   * Useful for role-gated apps (e.g., admin panel visible only to admins).
   *
   * @returns Whether the icon should be visible
   */
  visible?: () => boolean;
  /**
   * Dock priority controls the default placement of icons when the dock has
   * limited space. Icons are sorted by descending priority to fill the dock.
   *
   * - **Positive** (1, 2, 3…): higher values fill the dock first
   * - **0** (default): fills dock only if space remains after prioritized icons
   * - **Negative** (-1, -2…): Takeoff-only by default, never auto-placed in dock
   */
  dockPriority?: number;
}

/* ==========================================================================
   BACKGROUND CONFIGURATION - Discriminated Union
   ========================================================================== */

/**
 * Solid color background.
 */
export interface ColorBackground {
  type: "color";
  /** CSS color value (hex, rgb, hsl, named color) */
  color: string;
  /** Optional overlay for better icon visibility */
  overlay?: string;
}

/**
 * Image background.
 */
export interface ImageBackground {
  type: "image";
  /** URL of the background image */
  url: string;
  /** CSS background-size value (default: 'cover') */
  size?: "cover" | "contain" | "auto" | string;
  /** CSS background-position value (default: 'center') */
  position?: string;
  /** Optional overlay for better icon visibility */
  overlay?: string;
}

/**
 * Gradient background.
 */
export interface GradientBackground {
  type: "gradient";
  /** CSS gradient value (e.g., 'linear-gradient(to bottom, #1a1a2e, #16213e)') */
  gradient: string;
  /** Optional overlay for better icon visibility */
  overlay?: string;
}

/**
 * Background configuration - discriminated union ensures type/value correspondence.
 *
 * @example
 * ```ts
 * // Color background
 * const bg1: BackgroundConfig = { type: 'color', color: '#1a1a2e' };
 *
 * // Image background
 * const bg2: BackgroundConfig = { type: 'image', url: '/wallpaper.jpg' };
 *
 * // Gradient background
 * const bg3: BackgroundConfig = {
 *   type: 'gradient',
 *   gradient: 'linear-gradient(to bottom, #1a1a2e, #16213e)',
 * };
 * ```
 */
export type BackgroundConfig =
  | ColorBackground
  | ImageBackground
  | GradientBackground;

/* ==========================================================================
   ICON LAYOUT CONFIGURATION
   ========================================================================== */

/**
 * Layout configuration for desktop icons.
 */
export interface IconLayoutConfig {
  /**
   * Starting position for icons.
   * - 'top-left', 'top-right', 'bottom-left', 'bottom-right': Traditional desktop icon layout
   * - 'dock': macOS-style dock centered at the bottom of the screen (ignores direction)
   */
  startPosition:
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right"
    | "dock";
  /** Direction to arrange icons (ignored when startPosition is 'dock') */
  direction: "vertical" | "horizontal";
  /** Gap between icons in pixels */
  gap: number;
  /** Padding from the edge of the screen */
  padding: number;
}

/* ==========================================================================
   DESKTOP CONFIGURATION
   ========================================================================== */

/**
 * Complete desktop configuration.
 *
 * @remarks
 * This is the main configuration object that users pass to the Desktop
 * component. It defines all windows, icons, and desktop settings.
 *
 * @example
 * ```tsx
 * const config: DesktopConfig = {
 *   windows: [
 *     { id: 'terminal', type: 'app', title: 'Terminal', component: Terminal },
 *     { id: 'browser', type: 'browser', title: 'Browser', url: '/web' },
 *   ],
 *   icons: [
 *     { id: 'term-icon', windowId: 'terminal', icon: <Terminal />, label: 'Terminal' },
 *     { id: 'web-icon', windowId: 'browser', icon: <Globe />, label: 'Web' },
 *   ],
 *   background: {
 *     type: 'gradient',
 *     gradient: 'linear-gradient(to bottom, #1a1a2e, #16213e)',
 *   },
 * }
 *
 * <Desktop config={config} />
 * ```
 */
/**
 * Opt-in defaults that pin the initial icon layout to an explicit author-
 * specified configuration instead of the auto-derivation in buildDefaults.
 * Passed to buildDefaults via DesktopConfig.defaultIconPositions.
 *
 * Validation rules:
 *   - Throws on duplicate ids within desktop.dockOrder, within mobile.dock, or
 *     across mobile.dock + mobile.grids.
 *   - Throws if pinned icons (config entries with `pinned`) are referenced.
 *   - Throws on mobile.dock length > 4.
 *   - Throws on mobile.grids rows outside 0-4, cols outside 0-3 (4×5 grid).
 *   - Throws if mobile.grids pages aren't 0-indexed and sequential with
 *     ≥1 icon per page, or if two grid entries share a {page, row, col}.
 *   - Unknown ids (icon currently filtered out by visibility/install state)
 *     log a console.warn and are skipped — not fatal.
 *
 * Anything non-pinned that isn't referenced here is handled as:
 *   - Desktop: absent ids land in hiddenIcons (reachable via Takeoff/search).
 *   - Mobile: absent ids are auto-appended to the end of the last home-screen
 *     page (spilling to a new page if the last is full).
 */
export interface DefaultIconPositions {
  desktop?: {
    /** Icon ids in dock order. Everything else (non-pinned) is hidden. */
    dockOrder: string[];
  };
  mobile?: {
    /** Up to 4 ids; `null` leaves a blank dock slot at that index. */
    dock: (string | null)[];
    /** Sparse grid placements. Key = icon id, value = position. */
    grids: Record<string, { page: number; row: number; col: number }>;
  };
}

export interface DesktopConfig {
  /** Array of window configurations */
  windows: WindowConfig[];
  /** Array of desktop icon configurations */
  icons: DesktopIconConfig[];
  /**
   * Optional: author-specified initial icon layout. When present, takes
   * priority over auto-derivation in buildDefaults. When omitted (or the
   * authoring flag is off), buildDefaults falls back to its default
   * derivation. See DefaultIconPositions for validation rules.
   */
  defaultIconPositions?: DefaultIconPositions;
  /**
   * Background configuration (optional).
   * Used when darkBackground/lightBackground are not provided.
   */
  background?: BackgroundConfig;
  /**
   * Background for dark theme (optional).
   * Takes precedence over `background` when in dark mode.
   */
  darkBackground?: BackgroundConfig;
  /**
   * Background for light theme (optional).
   * Takes precedence over `background` when in light mode.
   */
  lightBackground?: BackgroundConfig;
  /** Icon layout configuration (optional) */
  iconLayout?: IconLayoutConfig;
  /** Auto-open a window on mount (by window ID) */
  autoOpenWindow?: string;
  /** Callback when desktop is ready */
  onReady?: () => void;
}

/* ==========================================================================
   WINDOW STATE - State Machine Approach (Illegal States Unrepresentable)
   ========================================================================== */

/**
 * Window is closed - not visible, not interactive.
 */
export interface ClosedWindowState {
  status: "closed";
}

/**
 * Window is open and visible.
 */
export interface OpenWindowState {
  status: "open";
  /** Whether this is the active/focused window */
  isActive: boolean;
  /** Whether the window is maximized to full screen */
  isMaximized: boolean;
  /** Z-index for stacking order */
  zIndex: number;
}

/**
 * Window is minimized (hidden but not closed).
 */
export interface MinimizedWindowState {
  status: "minimized";
  /** Z-index for when it's restored */
  zIndex: number;
}

/**
 * Window state - discriminated union prevents illegal state combinations.
 *
 * @remarks
 * Using a state machine approach ensures that:
 * - A closed window cannot be active or maximized
 * - A minimized window cannot be active or maximized
 * - Only open windows can be active or maximized
 *
 * @example
 * ```ts
 * // Check window status
 * if (state.status === 'open') {
 *   console.log('Active:', state.isActive);
 *   console.log('Maximized:', state.isMaximized);
 * } else if (state.status === 'minimized') {
 *   console.log('Window is minimized');
 * } else {
 *   console.log('Window is closed');
 * }
 * ```
 */
export type WindowState =
  | ClosedWindowState
  | OpenWindowState
  | MinimizedWindowState;

/**
 * Type guard: Check if window is closed.
 */
export function isWindowClosed(state: WindowState): state is ClosedWindowState {
  return state.status === "closed";
}

/**
 * Type guard: Check if window is open.
 */
export function isWindowOpen(state: WindowState): state is OpenWindowState {
  return state.status === "open";
}

/**
 * Type guard: Check if window is minimized.
 */
export function isWindowMinimized(
  state: WindowState
): state is MinimizedWindowState {
  return state.status === "minimized";
}

/**
 * Helper to create initial closed state.
 */
export function createClosedState(): ClosedWindowState {
  return { status: "closed" };
}

/**
 * Helper to create open state.
 */
export function createOpenState(
  isActive: boolean = false,
  isMaximized: boolean = false,
  zIndex: number = 1
): OpenWindowState {
  return { status: "open", isActive, isMaximized, zIndex };
}

/**
 * Helper to create minimized state.
 */
export function createMinimizedState(zIndex: number = 1): MinimizedWindowState {
  return { status: "minimized", zIndex };
}

/* ==========================================================================
   WINDOW ACTIONS
   ========================================================================== */

/**
 * Actions available for controlling a window.
 */
export interface WindowActions {
  /** Open the window */
  open: () => void;
  /** Close the window */
  close: () => void;
  /** Minimize the window */
  minimize: () => void;
  /** Restore from minimized state */
  restore: () => void;
  /** Maximize the window to full screen */
  maximize: () => void;
  /** Restore from maximized state */
  unmaximize: () => void;
  /** Toggle maximized state */
  toggleMaximize: () => void;
  /** Bring window to front and focus */
  focus: () => void;
}

/**
 * Combined state and actions for a window.
 */
export interface WindowInstance {
  /** Configuration for this window */
  config: WindowConfig;
  /** Current runtime state */
  state: WindowState;
  /** Actions to control the window */
  actions: WindowActions;
}

/* ==========================================================================
   SHARED WINDOW MANAGER INTERFACE
   ========================================================================== */

/**
 * Core window manager functionality shared between context and hook.
 */
export interface WindowManagerCore {
  /** Map of window ID to window instance */
  windows: Map<string, WindowInstance>;
  /** Open a window by ID */
  openWindow: (id: string) => void;
  /** Close a window by ID */
  closeWindow: (id: string) => void;
  /** Minimize a window by ID */
  minimizeWindow: (id: string) => void;
  /** Restore a window by ID */
  restoreWindow: (id: string) => void;
  /** Focus/activate a window by ID */
  focusWindow: (id: string) => void;
  /** Deactivate all windows (click on desktop background) */
  deactivateAll: () => void;
  /** Close all windows and reset state (e.g., on logout) */
  closeAllWindows: () => void;
}

/* ==========================================================================
   CONTEXT TYPES
   ========================================================================== */

/**
 * Desktop context value provided to all child components.
 */
export interface DesktopContextValue extends WindowManagerCore {
  /** Get the icon ref for a specific icon ID */
  getIconRef: (iconId: string) => RefObject<HTMLDivElement> | null;
  /** Register an icon ref */
  registerIconRef: (iconId: string, ref: RefObject<HTMLDivElement>) => void;
  /** Whether the current device is mobile */
  isMobile: boolean;
  /**
   * Claim a cascade slot for a window opening. Returns the lowest slot
   * index not currently held by another open window, so successive opens
   * land on distinct cascaded positions and re-using a slot freed by a
   * previous close keeps the window count compact (no drifting toward
   * the corner just because earlier windows have come and gone).
   */
  acquireCascadeSlot: (windowId: string) => number;
  /**
   * Release a previously-claimed cascade slot when a window closes, so
   * the next open can reuse that position.
   */
  releaseCascadeSlot: (windowId: string) => void;
  /** Current icon layout mode */
  layoutMode?: import("@repo/types").IconLayoutMode;
  /** Change the icon layout mode */
  setLayoutMode?: (mode: import("@repo/types").IconLayoutMode) => void;
  /**
   * Reset icon preferences to defaults for the specified surface only.
   * "desktop" leaves mobile grid/dock/groups intact; "mobile" leaves
   * desktop dock/placements intact.
   */
  resetIconPreferences?: (scope: "desktop" | "mobile") => void;
  /** True when the mobile layout currently matches config defaults. */
  isMobileLayoutAtDefaults?: boolean;
  /** True when the desktop layout currently matches config defaults. */
  isDesktopLayoutAtDefaults?: boolean;
  /** Whether menu bar hides when a window is maximized */
  hideMenuBarInFullScreen?: boolean;
  setHideMenuBarInFullScreen?: (v: boolean) => void;
  /** Selected wallpaper ID */
  backgroundId?: string;
  setBackgroundId?: (id: string) => void;
  /** Selected Shiki syntax-highlight theme family id */
  shikiTheme?: string;
  setShikiTheme?: (id: string) => void;
  /** CodeEditor: run Prettier/Ruff automatically when saving a file */
  codeEditorFormatOnSave?: boolean;
  setCodeEditorFormatOnSave?: (v: boolean) => void;
  /** User's saved custom Pexels photo IDs */
  customPexelsIds?: string[];
  addCustomPexelsId?: (id: string) => void;
  removeCustomPexelsId?: (id: string) => void;
  /** Whether desktop preferences are still loading */
  desktopPreferencesLoading?: boolean;
}

/* ==========================================================================
   HOOK RETURN TYPES
   ========================================================================== */

/**
 * Persisted geometry state for a window.
 */
export interface WindowGeometryState {
  position?: { x: number; y: number };
  size?: { width: number; height: number };
  savedPosition?: { x: number; y: number };
  savedSize?: { width: number; height: number };
  previousGeometry?: { x: number; y: number; width: number; height: number };
  isSnapped?: "left" | "right" | null;
}

/**
 * Return type for the useWindowManager hook.
 */
export interface UseWindowManagerReturn extends WindowManagerCore {
  /** Map of window ID to geometry state */
  geometryStates: Map<string, WindowGeometryState>;
  /** Update geometry for a specific window */
  updateGeometry: (
    windowId: string,
    geometry: Partial<WindowGeometryState>
  ) => void;
  /** Clear persisted geometry for a window so it reopens at default position */
  clearGeometry: (windowId: string) => void;
}

/* ==========================================================================
   COMPONENT PROPS
   ========================================================================== */

/**
 * Props for the main Desktop component.
 */
export interface DesktopProps {
  /** Desktop configuration */
  config: DesktopConfig;
  /** Children to render (for custom content on desktop) */
  children?: ReactNode;
  /** Custom class name for the desktop container */
  className?: string;
}

/**
 * Props for the Window component.
 */
export interface WindowComponentProps {
  /** Window configuration */
  config: WindowConfig;
  /** Current window state */
  state: WindowState;
  /** Window control actions */
  actions: WindowActions;
  /** Icon position for minimize animation */
  iconPosition?: { x: number; y: number };
  /** Get current icon position (for animation) */
  getIconPosition?: () => { x: number; y: number };
  /** Whether the device is mobile */
  isMobile?: boolean;
}

/**
 * Props for the DesktopIcon component.
 */
export interface DesktopIconComponentProps {
  /** Icon configuration */
  config: DesktopIconConfig;
  /** Click handler (opens the linked window) */
  onClick: () => void;
}

/* ==========================================================================
   VALIDATION
   ========================================================================== */

/**
 * Result of configuration validation.
 */
export interface ValidationResult {
  /** Whether the configuration is valid */
  valid: boolean;
  /** Array of error messages (empty if valid) */
  errors: string[];
  /** Array of warning messages (non-fatal issues) */
  warnings: string[];
}

/**
 * Validates a desktop configuration and returns detailed error information.
 *
 * @remarks
 * This function checks for:
 * - Duplicate window IDs
 * - Duplicate icon IDs
 * - Icons referencing non-existent windows
 * - Invalid autoOpenWindow references
 * - Missing required fields
 *
 * @example
 * ```ts
 * const result = validateDesktopConfig(config);
 * if (!result.valid) {
 *   console.error('Config errors:', result.errors);
 * }
 * if (result.warnings.length > 0) {
 *   console.warn('Config warnings:', result.warnings);
 * }
 * ```
 */
export function validateDesktopConfig(config: DesktopConfig): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const windowIds = new Set<string>();
  const iconIds = new Set<string>();

  // Validate windows array exists
  if (!config.windows || !Array.isArray(config.windows)) {
    errors.push('Config must have a "windows" array');
    return { valid: false, errors, warnings };
  }

  // Validate icons array exists
  if (!config.icons || !Array.isArray(config.icons)) {
    errors.push('Config must have an "icons" array');
    return { valid: false, errors, warnings };
  }

  // Check for duplicate window IDs and validate window configs
  for (const window of config.windows as Array<
    Partial<WindowConfig> & { id?: string }
  >) {
    if (!window.id) {
      errors.push('Window is missing required "id" field');
      continue;
    }

    if (windowIds.has(window.id)) {
      errors.push(`Duplicate window ID: "${window.id}"`);
    }
    windowIds.add(window.id);

    if (!window.title) {
      warnings.push(`Window "${window.id}" is missing a title`);
    }

    if (!window.type) {
      errors.push(`Window "${window.id}" is missing required "type" field`);
    } else if (window.type === "app") {
      if (!(window as Partial<AppWindowConfig>).component) {
        errors.push(
          `App window "${window.id}" is missing required "component" field`
        );
      }
    } else if (window.type === "browser") {
      if (!(window as Partial<BrowserWindowConfig>).url) {
        errors.push(
          `Browser window "${window.id}" is missing required "url" field`
        );
      }
    } else {
      errors.push(`Window "${window.id}" has invalid type: "${window.type}"`);
    }

    // Validate dimensionPercentage on any breakpoint of the window's dimensions
    const dims = (window as Partial<WindowConfigBase>).dimensions;
    if (dims) {
      const buckets: Array<{ label: string; dp?: DimensionPercentage }> = [];
      if (dims.responsive === false) {
        buckets.push({ label: "dimensions", dp: dims.dimensionPercentage });
      } else if (dims.responsive === true) {
        buckets.push({
          label: "dimensions.mobile",
          dp: dims.mobile?.dimensionPercentage,
        });
        buckets.push({
          label: "dimensions.tablet",
          dp: dims.tablet?.dimensionPercentage,
        });
        buckets.push({
          label: "dimensions.desktop",
          dp: dims.desktop?.dimensionPercentage,
        });
      }
      for (const { label, dp } of buckets) {
        if (!dp) continue;
        const inRange = (n: unknown) =>
          typeof n === "number" && Number.isFinite(n) && n > 0 && n <= 100;
        if ("ratio" in dp) {
          if (!inRange(dp.ratio)) {
            errors.push(
              `Window "${window.id}" ${label}.dimensionPercentage.ratio must be a number in (0, 100]`
            );
          }
        } else {
          if (!inRange(dp.width) || !inRange(dp.height)) {
            errors.push(
              `Window "${window.id}" ${label}.dimensionPercentage.width/height must be numbers in (0, 100]`
            );
          }
        }
      }
    }
  }

  // Check for duplicate icon IDs and validate references
  for (const icon of config.icons) {
    if (!icon.id) {
      errors.push('Icon is missing required "id" field');
      continue;
    }

    if (iconIds.has(icon.id)) {
      errors.push(`Duplicate icon ID: "${icon.id}"`);
    }
    iconIds.add(icon.id);

    if (!icon.windowId) {
      errors.push(`Icon "${icon.id}" is missing required "windowId" field`);
    } else if (!windowIds.has(icon.windowId)) {
      errors.push(
        `Icon "${icon.id}" references non-existent window "${icon.windowId}"`
      );
    }

    if (!icon.label) {
      warnings.push(`Icon "${icon.id}" is missing a label`);
    }

    if (!icon.icon) {
      errors.push(`Icon "${icon.id}" is missing required "icon" field`);
    }
  }

  // Validate autoOpenWindow reference
  if (config.autoOpenWindow && !windowIds.has(config.autoOpenWindow)) {
    errors.push(
      `autoOpenWindow references non-existent window "${config.autoOpenWindow}"`
    );
  }

  // Check for windows without icons (warning only)
  for (const windowId of windowIds) {
    const hasIcon = config.icons.some((icon) => icon.windowId === windowId);
    if (!hasIcon) {
      warnings.push(
        `Window "${windowId}" has no icon - it can only be opened programmatically`
      );
    }
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Throws an error if the configuration is invalid.
 * Useful for failing fast during development.
 *
 * @example
 * ```ts
 * // Will throw if config is invalid
 * assertValidConfig(config);
 * ```
 */
export function assertValidConfig(config: DesktopConfig): void {
  const result = validateDesktopConfig(config);
  if (!result.valid) {
    throw new Error(
      `Invalid DesktopConfig:\n${result.errors.map((e) => `  - ${e}`).join("\n")}`
    );
  }
}
